const AUTOMATION_WINDOW_KEYS = [
  // Headless browsers
  '_phantom', '__nightmare', '_selenium', 'callPhantom', 'callSelenium', '_Selenium_IDE_Recorder',
  // Modern automation frameworks (≥ 2023)
  '__playwright', '__pwInitScripts', 'playwright',
  '__puppeteer', '__puppeteer_evaluation_script__',
  // Browser extensions / stealth tooling
  'domAutomation', 'domAutomationController',
];

const AUTOMATION_DOCUMENT_KEYS = [
  // Selenium / WebDriver
  '__webdriver_evaluate', '__selenium_evaluate', '__webdriver_script_function', '__webdriver_script_func',
  '__webdriver_script_fn', '__fxdriver_evaluate', '__driver_unwrapped', '__webdriver_unwrapped',
  '__driver_evaluate', '__selenium_unwrapped', '__fxdriver_unwrapped',
  // Playwright leaves these in some configurations
  '__playwright_target__',
];

const AUTOMATION_DOCUMENT_ELEMENT_ATTRIBUTES = ['selenium', 'webdriver', 'driver'];

export function detectAutomationHint(): boolean {
  return checkWindowKeys()
    || checkDocumentKeys()
    || checkChromeDriverKeys()
    || checkPlaywrightKeys()
    || checkExternalSignature()
    || checkDocumentElementAttributes()
    || checkChromiumWithoutChromeObject()
    || checkEmptyLanguages();
}

function checkWindowKeys(): boolean {
  return AUTOMATION_WINDOW_KEYS.some(key => Boolean((window as any)[key]));
}

function checkDocumentKeys(): boolean {
  return AUTOMATION_DOCUMENT_KEYS.some(key => Boolean((document as any)[key]));
}

// ChromeDriver injects keys prefixed with `cdc_` (e.g. cdc_adoQpoasnfa76pfcZLmcfl_Array)
// onto the document. The original implementation matched a different regex by accident.
function checkChromeDriverKeys(): boolean {
  for (const key in document) {
    if (key.startsWith('cdc_') || key.startsWith('$cdc_')) return true;
  }
  return false;
}

// Playwright / Puppeteer often expose a marker on the global Symbol registry.
function checkPlaywrightKeys(): boolean {
  try {
    const globalSymbols = Object.getOwnPropertySymbols(window).map(s => s.toString());
    return globalSymbols.some(s => s.includes('playwright') || s.includes('puppeteer'));
  } catch {
    return false;
  }
}

function checkExternalSignature(): boolean {
  try {
    return Boolean(window['external']) && window['external'].toString().indexOf('Sequentum') !== -1;
  } catch {
    return false;
  }
}

function checkDocumentElementAttributes(): boolean {
  return AUTOMATION_DOCUMENT_ELEMENT_ATTRIBUTES.some(attr =>
    document.documentElement.getAttribute(attr) !== null);
}

// Chromium-based UA but no `window.chrome` object is a strong headless / spoofing indicator.
function checkChromiumWithoutChromeObject(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const isChromium = ua.includes('chrome/') || ua.includes('chromium/');
  if (!isChromium) return false;
  return !(window as any).chrome;
}

// `navigator.languages = []` is a textbook Puppeteer/Playwright misconfig — real browsers
// always return at least one entry derived from system locale.
function checkEmptyLanguages(): boolean {
  try {
    const languages = navigator.languages;
    return Array.isArray(languages) && languages.length === 0;
  } catch {
    return false;
  }
}

const FRAMEWORK_PROPERTY_SCAN_LIMIT = 200;

export function detectFrontendFramework(configuredFramework: string | null): string {
  if (configuredFramework) return configuredFramework;
  try {
    if (document.querySelector('[ng-version], [ng-app]')) return 'angular';

    if (document.querySelector('[data-v-app], [data-vue]')) return 'vue';

    if (document.querySelector('[data-reactroot], [data-reactid]')) return 'react';

    const elements = document.querySelectorAll('*');
    const limit = Math.min(elements.length, FRAMEWORK_PROPERTY_SCAN_LIMIT);
    for (let i = 0; i < limit; i++) {
      const element = elements[i] as any;
      if (element.__vue_app__ || element.__vue__) return 'vue';
      for (const key of Object.keys(element)) {
        if (key.startsWith('__reactFiber') || key.startsWith('__reactProps') || key.startsWith('__reactInternalInstance')) {
          return 'react';
        }
      }
    }

    return 'other';
  } catch {
    return 'other';
  }
}

export function isCanvasSupported(): boolean {
  const elem = document.createElement('canvas');
  return !!(elem.getContext && elem.getContext('2d'));
}

export function isWebGlSupported(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch {
    return false;
  }
}

export function isInIframe(): boolean {
  return window.top !== window.self;
}

export function getAncestorOriginCount(): number {
  return (location as any).ancestorOrigins?.length ?? 0;
}
